import React from "react";

import { InfoSmIcon as InfoSm, Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { Button, IconControl } from "ds/Button";
import Dialog from "ds/Dialog";
import Modal from "ds/Modal";
import * as Sticker from "ds/Sticker";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { goToEnvironment } from "Libs/utils";
import {
  environmentSelector,
  environmentLoadingSelector
} from "Reducers/environment";
import {
  mergeIsLoadingSelector,
  mergeErrorsSelector
} from "Reducers/environment/actions/merge";
import { useAppSelector } from "Store/hooks";

import { useChildHasNewContainers } from "./hooks/useChildHasNewContainers";
import MergeContent from "./MergeContent";
import MergeContentSizing from "./MergeContentSizing";
import * as S from "./MergePane.styles";

type EnvironmentMergePaneProps = {
  isSizingApiEnabled?: boolean;
};

const EnvironmentMergePane = ({
  isSizingApiEnabled
}: EnvironmentMergePaneProps) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const { organizationId, projectId, environmentId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
    environmentId: string;
  }>();

  const environment = useSelectorWithUrlParams(environmentSelector);
  const parent = useSelectorWithUrlParams(environmentSelector, {
    environmentId: environment?.parent
  });
  const isEnvironmentLoading = useSelectorWithUrlParams(
    environmentLoadingSelector
  );

  const isLoading = useAppSelector(mergeIsLoadingSelector);
  const errors = useAppSelector(mergeErrorsSelector);

  const childEnvHasNewContainers = useChildHasNewContainers();

  const MergeModalContent =
    isSizingApiEnabled && childEnvHasNewContainers
      ? MergeContentSizing
      : MergeContent;

  const onClose = (): void => {
    goToEnvironment(navigate, organizationId, projectId, environmentId);
  };

  const cliDialog = (
    <Dialog
      side="right"
      align="center"
      title={intl.formatMessage({ id: "learnmore" })}
      description={intl.formatMessage(
        { id: "merge.dialog.cli.description" },
        {
          currentEnvironment: environment?.title,
          parentEnvironment: parent?.title
        }
      )}
      analyticId="merge.dialog.cli.description"
      link={
        <Button
          analyticId="help_text"
          href={intl.formatMessage({
            id: "links.documentation.help.CLI"
          })}
          variant="link"
        >
          {intl.formatMessage({ id: "help_text", defaultMessage: "Help" })}
        </Button>
      }
      trigger={
        <IconControl>
          <InfoSm />
        </IconControl>
      }
    />
  );

  return (
    <Modal
      isOpen={true}
      size="medium"
      onOpenChange={onClose}
      handleClose={onClose}
    >
      <div>
        <Typography.Title tag="h3">
          {intl.formatMessage(
            { id: "merge.title" },
            {
              parentEnvironment: parent?.title
            }
          )}
        </Typography.Title>
        {typeof isSizingApiEnabled === "undefined" ? (
          <S.Loading />
        ) : (
          <div>
            {!!errors && (
              <Sticker.Root priority="critical">
                <Sticker.Icon priority="critical" />
                <Sticker.Text>{errors}</Sticker.Text>
              </Sticker.Root>
            )}
            <MergeModalContent
              parent={parent}
              environment={environment}
              organizationId={organizationId}
              projectId={projectId}
              environmentId={environmentId}
              isLoading={isLoading || isEnvironmentLoading}
              cliDialog={cliDialog}
              isSizingApiEnabled={isSizingApiEnabled}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default EnvironmentMergePane;
